/**
 * Web Push subscription management.
 * Requests Notification permission, subscribes via PushManager, and sends
 * the subscription object to the server.
 */

import { apiFetch } from './api.js';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function fetchVapidPublicKey(): Promise<string | null> {
  try {
    const res = await apiFetch('/api/push/vapid-key');
    if (!res.ok) return null;
    const data = (await res.json()) as { publicKey: string };
    return data.publicKey;
  } catch {
    return null;
  }
}

/** Subscribe to push notifications and register with the server. No-op if already subscribed. */
export async function subscribeToPush(playerId: string): Promise<void> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  if (Notification.permission === 'denied') return;

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;

    const reg = await navigator.serviceWorker.ready;
    const publicKey = await fetchVapidPublicKey();
    if (!publicKey) return;

    // Reuse existing subscription if valid
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey).buffer as ArrayBuffer,
      });
    }

    await apiFetch('/api/push/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId, subscription: sub.toJSON() }),
    });
  } catch (err) {
    console.warn('[Push] Subscribe failed:', err);
  }
}

/** Clear the app badge. */
export function clearBadge(): void {
  if ('clearAppBadge' in navigator) {
    void (navigator as Navigator & { clearAppBadge(): Promise<void> }).clearAppBadge();
  }
}
